/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import SimpleModal from '@broadleaf/admin-components/dist/common/components/SimpleModal';
import Spinner from '@broadleaf/admin-components/dist/common/elements/Spinner';
import RMToggleSwitch from '@src/components/form/toggle-switch/RMToggleSwitch';
import useFetchConsolidatedOrder from '@src/hooks/useFetchConsolidatedOrder';
import useContextParams from '@src/oms/FulfillmentView/hooks/useContextParams';
import { IMetadata } from '@broadleaf/admin-components/dist/types/metadata';
import { map, set, some } from 'lodash';
import { Button, IconButton } from '@broadleaf/admin-components/dist/common';
import { InvoicesExportConfiguration } from '../action-components/InvoicesExportAction/InvoicesExportAction';
import { useAuthContext } from '@broadleaf/admin-components/dist/authentication';
import { IAccount, ICustomer } from '@src/oms/utils/RmCustomerUtils';
import useFetchCustomer from '@src/hooks/useFetchCustomer';
import useFetchAccount from '@src/hooks/useFetchAccount';
import useFetchCustomers from '@src/hooks/useFetchCustomers';

export const InvoicesExportModal: React.FC<SubmitInvoicesExportModalProps> = props => {
  const { allSelectedItems, onClose, metadata } = props;
  const [config, setConfig] = useState<InvoicesExportConfiguration>();
  const [isLoadingOrder, setIsLoadingOrder] = useState<boolean>(true);
  const [error, setError] = useState<string>();
  const { fetchConsolidatedOrder } = useFetchConsolidatedOrder();
  const contextParams = useContextParams(metadata);
  const { user } = useAuthContext();
  const submitDisabled = !config;

  const [account, setAccount] = useState<IAccount>();
  const [customer, setCustomer] = useState<ICustomer>();

  const { fetchCustomer } = useFetchCustomer();
  const { fetchCustomers } = useFetchCustomers();
  const { fetchAccount } = useFetchAccount();

  const loadInvoiceExportModalData = useCallback(
    async (orderId: string) => {
      const consolidatedOrder = await fetchConsolidatedOrder(
        orderId,
        contextParams
      );

      const invoiceAccount =
        consolidatedOrder.accountId &&
        (await fetchAccount(consolidatedOrder.accountId, contextParams));
      setAccount(invoiceAccount);

      const invoiceCustomer =
        !consolidatedOrder.accountId &&
        (await fetchCustomer(consolidatedOrder.customerId, contextParams));
      setCustomer(invoiceCustomer);

      const salesRepEmail =
        invoiceCustomer?.salesRepEmail || invoiceAccount?.salesRepEmail;
      let salesRepCustomer = null;
      if (salesRepEmail) {
        try {
          const customersResponse = await fetchCustomers(
            'email=eqic=' + salesRepEmail,
            contextParams
          );
          if (customersResponse && customersResponse.numberOfElements > 0) {
            salesRepCustomer = customersResponse.content[0];
          }
        } catch (error) {
          console.log('Unable to find salesrep', error);
        }
      }

      const recipients = [];
      const invoiceCreditAccount = consolidatedOrder.invoiceCreditAccount;
      if (invoiceCreditAccount) {
        if (invoiceCreditAccount.attributes.apContactEmail) {
          recipients.push({
            name: invoiceCreditAccount.attributes.apContactName,
            email: invoiceCreditAccount.attributes.apContactEmail,
            recipientCategory: 'to'
          });
        }
        if (invoiceCreditAccount.attributes.purchaserContactEmail) {
          recipients.push({
            name: invoiceCreditAccount.attributes.purchaserContactName,
            email: invoiceCreditAccount.attributes.purchaserContactEmail,
            recipientCategory: 'to'
          });
        }
        const additionalApContacts = (invoiceCreditAccount.attributes
          .additionalApContacts || []) as Array<{
          email: string;
          name: string;
        }>;
        for (const additionalApContact of additionalApContacts) {
          recipients.push({
            name: additionalApContact.name,
            email: additionalApContact.email,
            recipientCategory: 'to'
          });
        }
      } else {
        recipients.push({
          name: invoiceCustomer.fullName,
          email: invoiceCustomer.email
        });
      }
      recipients.push({
        name: user.fullName,
        email: user.email,
        recipientCategory: 'cc'
      });

      if (
        salesRepCustomer &&
        !some(
          recipients,
          recipient => recipient.email === salesRepCustomer.email
        )
      ) {
        recipients.push({
          name: salesRepCustomer.name,
          email: salesRepCustomer.email,
          recipientCategory: 'cc'
        });
      }

      // Don't use/send customer ID if account ID exists to prevent erroneous filtering to customer
      const customerId = consolidatedOrder.accountId
        ? null
        : consolidatedOrder.customerId;
      setConfig({
        accountId: consolidatedOrder.accountId,
        customerId: customerId,
        emailNotes: '',
        exampleOrderId: consolidatedOrder.id,
        exportType: 'email',
        includeStatementOfAccount: true,
        includePaymentInstructions: false,
        includeInvoicePdfs: false,
        invoiceIds: allSelectedItems.map(item => (item as any).id),
        recipients
      });
      setIsLoadingOrder(false);
    },
    [
      allSelectedItems,
      contextParams,
      fetchAccount,
      fetchConsolidatedOrder,
      fetchCustomer,
      fetchCustomers,
      user.email,
      user.fullName
    ]
  );

  useEffect(() => {
    loadInvoiceExportModalData(allSelectedItems[0].id);
  }, [allSelectedItems[0].id]);

  const validate = useCallback(config => {
    if (!config) {
      return 'Config is invalid';
    }
    if (!config.includeInvoicePdfs && !config.includeStatementOfAccount) {
      return 'Either include Invoice PDFs or inlude Statement of Account must be selected';
    }
    if (config.recipients.length === 0) {
      return 'No recipients provided';
    }
    if (config.exportType === 'email') {
      if (config.recipients.length === 0) {
        return 'No recipients provided';
      }
      for (const recipient of config.recipients) {
        if (!recipient.email) {
          return 'Email is required for every additional recipient';
        }
      }
      if (config.includeInvoicePdfs && config.invoiceIds.length > 10) {
        return 'Only 10 invoices can be sent at a time to avoid attachments being too large';
      }
    }
    return null;
  }, []);

  return (
    <SimpleModal
      /*// @ts-ignore */
      closeOnClickOutside={false}
      isOpen
      onClose={onClose}
      size="lg"
      title={<>Export Invoices</>}
      footer={
        <div className="tw-align-left tw-flex tw-w-full tw-items-center tw-text-red-500">
          <div className="tw-flex-grow">{error || ' '}</div>
          <div className="tw-flex-grow-0">
            <SubmitButton
              isDisabled={submitDisabled}
              submitLabel={'Submit'}
              isSubmitting={props.isSubmitting}
              config={config}
              onSubmit={() => {
                const error = validate(config);
                setError(error);
                if (!error) {
                  props.exportInvoices(config);
                }
              }}
            />
          </div>
        </div>
      }
    >
      {!isLoadingOrder && (
        <ModalBody
          {...props}
          config={config}
          setConfig={setConfig}
          account={account}
          customer={customer}
        />
      )}
      {isLoadingOrder && (
        <Spinner
          size="md"
          className="tw-ml-2"
          innerClassName="tw-border-gray-100"
        />
      )}
    </SimpleModal>
  );
};

export interface SubmitInvoicesExportModalProps {
  metadata: IMetadata;
  allSelectedItems: any[];
  exportInvoices: (config: InvoicesExportConfiguration) => void;
  isSubmitting: boolean;
  onClose: () => void;
}

const ModalBody = props => {
  const { allSelectedItems, config, setConfig, account, customer } = props;

  const exportTargetOptions = [
    {
      label: 'Email',
      value: 'email'
    },
    {
      label: 'Download',
      value: 'download'
    }
  ];
  const recipientReceiveOptions = [
    {
      label: 'To',
      value: 'to'
    },
    {
      label: 'CC',
      value: 'cc'
    },
    {
      label: 'BCC',
      value: 'bcc'
    }
  ];

  const handleCheckboxChange = e => {
    const { name, checked } = e.target;
    setConfig(prevState => {
      return { ...prevState, [name]: checked };
    });
  };

  const handleTextChange = e => {
    const { name, value } = e.target;
    setConfig(prevState => {
      const newState = { ...prevState };
      set(newState, name, value);
      return newState;
    });
  };

  if (!config) {
    return (
      <div>There was an error pulling details for the invoices export.</div>
    );
  }

  return (
    <div>
      {account && <div className="tw-pb-2">Account: {account.name}</div>}
      {customer && (
        <div className="tw-pb-2">
          Customer: {customer.fullName} ({customer.email})
        </div>
      )}
      <div>
        <input
          className={'mr-2'}
          type={'checkbox'}
          name={'includeStatementOfAccount'}
          onChange={handleCheckboxChange}
          checked={config.includeStatementOfAccount}
        />
        <label htmlFor={'includeStatementOfAccount'}>
          Include statement of account?
        </label>
      </div>
      <div>
        <input
          className={'mr-2'}
          type={'checkbox'}
          name={'includeInvoicePdfs'}
          onChange={handleCheckboxChange}
          checked={config.includeInvoicePdfs}
        />
        <label htmlFor={'includeStatementOfAccount'}>
          Include {allSelectedItems.length} selected invoice PDF
          {allSelectedItems.length > 1 && 's'}?
        </label>
      </div>
      <RMToggleSwitch
        className={'mt-1'}
        options={exportTargetOptions}
        value={config.exportType}
        onChange={value => {
          setConfig(prevState => {
            return { ...prevState, exportType: value };
          });
        }}
      />
      {config.exportType === 'email' && (
        <div className="mt-2">
          <input
            className={'mr-2'}
            type={'checkbox'}
            name={'includePaymentInstructions'}
            onChange={handleCheckboxChange}
            checked={config.includePaymentInstructions}
          />
          <label htmlFor={'includePaymentInstructions'}>
            Include payment instructions PDF?
          </label>
        </div>
      )}
      {config.exportType === 'email' && (
        <div>
          <h2 className="mt-2 tw-text-lg">Email Recipients</h2>
          {map(config.recipients, (recipient, index) => {
            return (
              <div key={index} className="mb-1">
                <input
                  className="border p-1 mr-1"
                  type="text"
                  autoComplete={'off'}
                  name={`recipients[${index}].name`}
                  required={false}
                  onChange={handleTextChange}
                  value={recipient.name}
                  placeholder={'Name'}
                />
                <input
                  className="border p-1 mr-1"
                  type="text"
                  autoComplete={'off'}
                  name={`recipients[${index}].email`}
                  required={true}
                  onChange={handleTextChange}
                  value={recipient.email}
                  placeholder={'Email'}
                />
                <RMToggleSwitch
                  className={'mt-1'}
                  options={recipientReceiveOptions}
                  value={recipient.recipientCategory}
                  onChange={value => {
                    setConfig(prevState => {
                      const newState = { ...prevState };
                      set(
                        newState,
                        `recipients[${index}].recipientCategory`,
                        value
                      );
                      return newState;
                    });
                  }}
                />
                <IconButton
                  label="Delete"
                  name="minus"
                  shape="square"
                  size="sm"
                  color="danger-clear"
                  onClick={() => {
                    setConfig(prevState => {
                      return {
                        ...prevState,
                        recipients: prevState.recipients.toSpliced(index, 1)
                      };
                    });
                  }}
                />
              </div>
            );
          })}
          <Button
            color="primary"
            onClick={() => {
              setConfig(prevState => {
                return {
                  ...prevState,
                  recipients: [...prevState.recipients, {}]
                };
              });
            }}
          >
            Add Recipient
          </Button>
          <div>
            <h2 className="mt-2 tw-text-lg">Email Notes</h2>
            <textarea
              className="border tw-w-full"
              rows={4}
              name={`emailNotes`}
              required={false}
              onChange={handleTextChange}
              value={config.emailNotes}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const SubmitButton = ({
  isSubmitting,
  submitLabel,
  isDisabled = false,
  onSubmit,
  config
}) => {
  return (
    <>
      <button
        disabled={isSubmitting || isDisabled}
        className={classNames(
          'tw-text-md focus:tw-shadow-outline tw-w-full tw-rounded tw-border tw-bg-primary-500 tw-px-4 tw-py-4 tw-font-semibold tw-text-primary-100 tw-shadow hover:tw-bg-primary-600 focus:tw-outline-none md:tw-w-auto md:tw-py-2',
          {
            'tw-cursor-not-allowed': isSubmitting || isDisabled
          }
        )}
        onClick={() => onSubmit(config)}
        style={{ opacity: isSubmitting ? '0.65' : '1' }}
        type="submit"
      >
        {' '}
        {submitLabel}{' '}
        {isSubmitting && (
          <Spinner
            size="md"
            className="tw-ml-2"
            innerClassName="tw-border-gray-100"
          />
        )}
      </button>
    </>
  );
};

export default InvoicesExportModal;
